import { Button, Typography } from "@mui/material";
import { updateTransfer } from "apis/controllers/transfers";
import { FileProvider, useFileContext } from "context/FilesV2";
import { useLoading } from "hooks/useLoading";
import ConfirmDialog from "qubCommon/Dialogs/ConfirmationDialog";
import FilePreviewer from "qubCommon/FilePreviewer";
import { useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteBundle,
  deleteFile,
  downloadFile,
  formatFiles,
  uploadFiles,
} from "util";
import AlertBanner from "./Components/AlertBanner";
import Amendment from "./Components/Amendment";
import ApartmentInfo from "./Components/ApartmentInfo";
import Attachments from "./Components/Attachments";
import ChildInfo from "./Components/ChildInfo";
import ChildMainInstitute from "./Components/ChildMainInstitute";
import History from "./Components/History";
import ListFileItem from "./Components/ListFileItem";
import SummaryCard from "./Components/SummaryCard";
import TransferActions from "./Components/TransferActions";
import SocialWorkerInfo from "./SocialWorkerInfo";
import "./TransferDetails.css";
import useTransferDetails from "./useTransferDetails";
import ApartmentSelection from "./Components/ApartmentSelection";
import CareUnitSelection from "./Components/CareUnitSelection";

const TransferDetails = () => {
  const { form, data, succeeded, files, fetchTransfer, resetForm, apartments } =
    useTransferDetails();

  const { id } = useParams();

  if (succeeded)
    return (
      <div className="transfers-container">
        <FileProvider
          downloadFile={(fileId, type) => downloadFile(fileId, type)}
          uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
          onDeleteBundle={async (bundleId) =>
            await deleteBundle(bundleId, 13, null, id, null)
          }
          onDeleteFile={async (fileId) => {
            await deleteFile(fileId, 13, null, id, null);
          }}
        >
          <Form
            form={form}
            data={data}
            files={files}
            fetchTransfer={fetchTransfer}
            resetForm={resetForm}
            apartments={apartments}
          />
        </FileProvider>
      </div>
    );
};

const Form = (props) => {
  const { form, data, files, fetchTransfer, resetForm } = props;
  const [openAmendment, setOpenAmendment] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    content: "",
    onConfirm: () => { },
  });
  const { id } = useParams();
  const { fileGroups, control } = useFileContext();
  const fgRefs = useRef([]);
  const { setLoading } = useLoading();
  const settings = data?.application?.settings;
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const formattedFiles = formatFiles(files);
      if (formattedFiles !== undefined) {
        control.initialize(formattedFiles);
      } else {
        control.initialize([]);
      }
    } catch (error) {
      console.error("error", error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const prepareBundles = () => {
    const bundleIds = fileGroups.reduce((acc, group) => {
      return [
        ...acc,
        ...group.bundles
          // keep bundles that contain at least 1 file with missing dbId
          .filter((bundle) =>
            bundle.files?.some((file) => file.dbId == null) // matches null or undefined
          )
          .map((bundle) => bundle.id),
      ];
    }, []);

    return { bundleIds };
  };

  const handleTransferUpdate = async (
    action,
    additionalData = {},
    uploadFiles = true
  ) => {
    try {
      setLoading(true);
      const transferData = action !== "approve" ? form.getValues() : { id };
      if (uploadFiles) {
        const resFiles = await control.uploadFileGroups();
        if (resFiles?.status === "error") {
          setErrorBanner(resFiles?.message);
          setLoading(false);
          return;
        }
      }

      const uploadData = {
        ...transferData,
        ...additionalData,
        action,
      };

      const res = await updateTransfer(uploadData);

      if (res?.status === "success") await fetchTransfer();
      if (res?.status === "error") {
        setErrorBanner(res?.message);
        if (action === "submit") resetForm();
      }

      setLoading(false);
      setOpenDialog(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
      setErrorBanner("<div>Προέκυψε Σφάλμα!</div>");
    }
  };

  const handleOpenDialog = (actionType) => {
    switch (actionType) {
      case "accept":
        setDialogProps({
          title: "Αποδοχή Τροποποιήσεων",
          content: "Θέλετε σίγουρα να αποδεχτείτε τις τροποποιήσεις;",
          onConfirm: handleAccept,
        });
        break;
      case "refuse":
        setDialogProps({
          title: "Απόρριψη Τροποποιήσεων",
          content: "Θέλετε σίγουρα να απορρίψετε τις τροποποιήσεις;",
          onConfirm: handleRefuse,
        });
        break;
      case "approve":
        setDialogProps({
          title: "Έγκριση Αίτησης",
          content: "Θέλετε σίγουρα να εγκρίνετε την αίτηση;",
          onConfirm: handleApprove,
          commentsOn: settings?.approve?.commentsOn,
          withFileUpload: settings?.approve?.withFileUpload,
          fileDatakey: settings?.approve?.fileDataKey,
          fileDataValue: settings?.approve?.fileDataValue,
        });
        break;
      case "reject":
        setDialogProps({
          title: "Απόρριψη Αίτησης",
          content: ` Θέλετε σίγουρα να επιστρέψετε την αίτηση στον αιτούντα; 
            Παρακαλώ αναφέρετε τους λόγους απόρριψης.`,
          onConfirm: settings?.reject?.withFileUpload
            ? handleRejectWithFileUpload
            : handleReject,
          commentsOn: true,
          withFileUpload: settings?.reject?.withFileUpload,
          fileDatakey: settings?.reject?.fileDataKey,
          fileDataValue: settings?.reject?.fileDataValue,
        });
        break;
      case "submit":
        setDialogProps({
          title: "Υποβολή Αίτησης",
          content: "Θέλετε σίγουρα να υποβάλλετε την αίτηση;",
          onConfirm: handleSubmit,
          withFileUpload: settings?.submit?.withFileUpload,
          fileDatakey: settings?.submit?.fileDataKey,
          fileDataValue: settings?.submit?.fileDataValue,
        });
        break;
      case "cancel":
        setDialogProps({
          title: "Ακύρωση Αίτησης",
          content: "Θέλετε σίγουρα να ακυρώσετε την αίτηση;",
          onConfirm: handleCancel,
        });
        break;
      case "backToHome":
        const dirtyFields = Object.keys(form.formState.dirtyFields).map(
          (x) => ({
            field: x,
            value: form.getValues(x),
          })
        );
        if (!!dirtyFields?.length || control.isDirty)
          setDialogProps({
            title: "Προσοχή!",
            content:
              "Έχετε πραγματοποιήσει αλλαγές στη φόρμα σας. Αν αποχωρήσετε οι αλλαγές θα χαθούν. Θέλετε να κλείσετε τη σελίδα;",
            onConfirm: () => navigate("/govnr/transfers"),
          });
        else navigate("/govnr/transfers");
        break;
      case "partiallyApprove":
        setDialogProps({
          title: settings?.partiallyApprove?.partiallyApproveBtnTxt,
          content:
            "Θέλετε σίγουρα να προχωρήσετε σε αλλαγή κατάστασης της αίτησης;",
          onConfirm: handlePartiallyApprove,
          withFileUpload: settings?.partiallyApprove?.withFileUpload,
          fileDatakey: settings?.partiallyApprove?.fileDataKey,
          fileDataValue: settings?.partiallyApprove?.fileDataValue,
        });
        break;
      default:
        return;
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = async () => {
    await setOpenDialog(false);
    await setDialogProps((prev) => ({ ...prev, withFileUpload: false }));
  };

  const handlePreviewFile = async (file) => {
    const res = await downloadFile(file?.id, file?.type ?? file?.contentType);
    setFileToPreview(res);
  };

  // Refactored handlers

  const handleSave = async () => {
    const { bundleIds } = prepareBundles();
    await handleTransferUpdate("save", {
      bundles: bundleIds,
    });
  };

  const handleAccept = async () => {
    await handleTransferUpdate("accept", { id: form.getValues("id") }, false);
  };

  const handleRefuse = async () => {
    await handleTransferUpdate("refuse", { id: form.getValues("id") }, false);
  };

  const handleApproveSave = async () => {
    try {
      setLoading(true);

      // ToDo SOS Uncomment this
      const resFiles = await control.uploadFileGroups();
      if (resFiles?.status === "error") {
        setErrorBanner(resFiles?.message);
        setLoading(false);
        return;
      }
      await fetchTransfer();

      setLoading(false);
    } catch {
      setLoading(false);
      setErrorBanner("<div>Προέκυψε σφάλμα!</div>");
    }
  };

  const handleApprove = async (fileBundles = [], comments) => {
    await handleTransferUpdate(
      "approve",

      fileBundles?.length > 0
        ? { bundles: fileBundles, comments }
        : { comments },
      true
    );
  };

  const handleRejectWithFileUpload = async (fileBundles = [], comments) => {
    const { bundleIds } = prepareBundles();
    const additionalData =
      fileBundles?.length > 0
        ? {
          id: form.getValues("id"),
          action: "reject",
          comments,
          bundles: fileBundles,
        }
        : {
          id: form.getValues("id"),
          action: "reject",
          comments,
        };
    await handleTransferUpdate("reject", additionalData, false);
  };

  const handleReject = async (comments) => {
    await handleTransferUpdate(
      "reject",
      {
        id: form.getValues("id"),
        comments,
      },
      true
    );
  };

  const handleSubmit = async (fileBundles = []) => {
    const { bundleIds } = prepareBundles();
    let submitBundles = bundleIds;
    if (fileBundles?.length > 0) submitBundles = [...bundleIds, ...fileBundles];
    await handleTransferUpdate(
      "submit",
      submitBundles?.length > 0
        ? {
          bundles: submitBundles,
        }
        : {}
    );
  };

  const handleCancel = async () => {
    await handleTransferUpdate("cancel", { id: form.getValues("id") }, false);
  };

  const handlePartiallyApprove = async (fileBundles = []) => {
    await handleTransferUpdate(
      "partially_approve",
      fileBundles?.length > 0
        ? {
          bundles: fileBundles,
        }
        : {},
      false
    );
  };

  return (
    <FormProvider {...form}>
      <FilePreviewer
        file={fileToPreview}
        onClose={() => setFileToPreview(null)}
      />
      <ConfirmDialog
        projectTableId={15}
        open={openDialog}
        title={dialogProps.title}
        content={dialogProps.content}
        onClose={handleCloseDialog}
        onConfirm={dialogProps.onConfirm}
        commentsOn={dialogProps.commentsOn}
        withFileUpload={dialogProps?.withFileUpload}
        fileDatakey={dialogProps?.fileDatakey}
        fileDataValue={dialogProps?.fileDataValue}
      />
      <AlertBanner canClose={false} info={data?.application?.status}>
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          {data?.application?.status?.files.map((file, index) => (
            <ListFileItem
              key={index}
              file={file}
              index={index}
              handlePreviewFile={() => handlePreviewFile(file)}
            />
          ))}
        </div>
        <div className="buttons-container">
          {data?.application?.status?.refuseBtnText && (
            <Button
              variant="contained"
              color="error"
              onClick={() => handleOpenDialog("refuse")}
            >
              {data?.application?.status?.refuseBtnText}
            </Button>
          )}
          {data?.application?.status?.acceptBtnText && (
            <Button
              variant="contained"
              onClick={() => handleOpenDialog("accept")}
            >
              {data?.application?.status?.acceptBtnText}
            </Button>
          )}
        </div>
      </AlertBanner>
      <SummaryCard
        protocolNumber={data?.application?.protocolNumber}
        status={data?.application?.status}
        created={data?.application?.created}
        updated={data?.application?.updated}
        submitted={data?.application?.submitted}
      />
      <ChildInfo child={data?.application?.child} />
      <ChildMainInstitute
        childInstitute={data?.application?.childMainInstitute}
        worker={data?.application?.socialWorker}
      />
      {/* <SocialWorkerInfo worker={data?.application?.socialWorker} /> */}
      {/* <ApartmentInfo apartment={data?.application?.apartment} /> */}

      {settings?.instituteSettings?.dataKey === 'Apartments' && <ApartmentSelection apartments={props.apartments} canChangeApartment={settings?.instituteSettings?.canEdit} child={data?.application?.child} />}
      {settings?.instituteSettings?.dataKey === 'ChildInstitutes' && <CareUnitSelection apartments={props.apartments} canChangeApartment={settings?.instituteSettings?.canEdit} child={data?.application?.child} />}

      <Attachments fileGroups={fileGroups} settings={settings?.attachments} />

      {data?.application?.applicantComments && (
        <div
          style={{
            marginTop: "24px",
            padding: "16px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #ddd",
            borderRadius: "8px",
            fontSize: "1rem",
            lineHeight: "1.6",
          }}
        >
          <Typography
            color="primary"
            variant="h5"
            style={{
              display: "block",
              marginBottom: "8px",
            }}
          >
            Σχόλιο Αιτούντα
          </Typography>
          {data.application.applicantComments}
        </div>
      )}
      <TransferActions
        handleCancel={() => handleOpenDialog("cancel")}
        canEdit={settings?.canEdit}
        canCancel={settings?.cancel?.can}
        canSubmit={settings?.submit?.can}
        settings={settings}
        handleSubmit={() => handleOpenDialog("submit")}
        handleApprove={() => handleOpenDialog("approve")}
        handleSave={() => handleSave()}
        handleApproveSave={() => handleApproveSave()}
        handleReject={() => handleOpenDialog("reject")}
        handlePartiallyApprove={() => handleOpenDialog("partiallyApprove")}
        handleAmendment={() => setOpenAmendment(true)}
        handleBackToHome={() => handleOpenDialog("backToHome")}
      />
      <AlertBanner
        openInfo={!!errorBanner}
        setOpenMessage={setErrorBanner}
        info={{
          message: `<div>${errorBanner}</div>`,
          cssClass: "error",
        }}
      />
      <History
        history={data?.application?.history}
        handlePreviewFile={handlePreviewFile}
        applicationId={id}
        onHistoryRevised={fetchTransfer}
        projectTableId={15}
      />
      <Amendment
        open={openAmendment}
        handleClose={() => setOpenAmendment(false)}
      />
    </FormProvider>
  );
};

export default TransferDetails;
